import {
  formInputInfo,
  FormState,
  NewMultiCertRequest,
  NewSingleCertRequest,
} from "../types/types";
import { LoopReducer, loop, Loop, Cmd } from "redux-loop";
import formService from "../services/forms";
import {
  getType,
  createAsyncAction,
  ActionType,
  createAction,
} from "typesafe-actions";
import { loginAsync } from "../login/loginReducer";
import {
  createEventAsync,
  getDraggableForm,
  getSubEventDetails,
} from "../events/eventReducer";
import {
  arrayToObject,
  arrayToObjectVariation,
  concatNarrays,
  getArrays,
} from "../utils/functions";
import {
  changeSelectedTemplate,
  newCertSend,
  setCertTemplateDefaultValues,
} from "../certs/certReducer";
import certService from "../services/certs";
import ConsoleHelper from "../utils/logger";

const initialState: FormState = {
  input: {
    loginForm: {},
    createNewEvent: {},
    selectRegistration: {},
    previewRegistrationForm: {},
    registrationInfo: {},
    newCert: {},
  },
  items: {
    loginForm: [],
    createNewEvent: [],
    selectRegistration: [],
    previewRegistrationForm: [],
  },
  initLoading: false,
};

export const getFormAsync = createAsyncAction(
  "START_FORM_FETCH",
  "FETCH_COMPLETE",
  "FETCH_FAIL"
)<{ form: string; language: string }, { data: any; status: number }, Error>();

//TODO: changing cert should empty certfields
const updateValue = createAction("UPDATEVALUE")<{
  form: string;
  id: string;
  value: string;
}>();
const updateDateValue = createAction("UPDATEDATEVALUE")<{
  form: string;
  id: string;
  value: Date;
}>();
const addToValue = createAction("ADDTOVALUE")<{
  form: string;
  id: string;
  value: string[];
}>();
const removeKey = createAction("REMOVEKEY")<{ form: string; id: string }>();
const setForm = createAction("SET_FORM")<{
  id: string;
  form: formInputInfo[];
}>();
const setFormValues = createAction("SET_FORM_VALUES")<{
  id: string;
  data: { [key: string]: string };
}>();

type Action =
  | ActionType<typeof getFormAsync>
  | ActionType<typeof loginAsync>
  | ActionType<typeof getDraggableForm>
  | ActionType<typeof getSubEventDetails>
  | ActionType<typeof newCertSend>
  | ActionType<typeof createEventAsync>
  | ActionType<typeof changeSelectedTemplate>
  | ActionType<typeof updateValue>
  | ActionType<typeof updateDateValue>
  | ActionType<typeof addToValue>
  | ActionType<typeof removeKey>
  | ActionType<typeof setForm>
  | ActionType<typeof setFormValues>
  | ActionType<typeof setCertTemplateDefaultValues>;

export const formReducer: LoopReducer<FormState, Action> = (
  state: FormState = initialState,
  action: Action
): FormState | Loop<FormState> => {
  switch (action.type) {
    case getType(getDraggableForm.success):
      const itemsTable = arrayToObject(
        action.payload.data.formData.inputs,
        "id"
      );
      const allBaskets = Object.keys(action.payload.data.formData.baskets);
      const previewList = concatNarrays(
        getArrays(allBaskets, action.payload.data.formData.baskets)
      ).map((id) => itemsTable[id]);
      return {
        ...state,
        items: {
          ...state.items,
          previewRegistrationForm: previewList,
        },
      };

    case getType(getSubEventDetails.success):
      return {
        ...state,
        items: {
          ...state.items,
          previewRegistrationForm: action.payload.data.registrationQuestions,
        },
      };

    case getType(createEventAsync.success):
      return {
        ...state,
        input: {
          ...state.input,
          createNewEvent: {
            eventOrganiser: state.input.createNewEvent.eventOrganiser,
          },
        },
      };

    case getType(setForm):
      return {
        ...state,
        items: {
          ...state.items,
          [action.payload.id]: action.payload.form,
        },
      };

    case getType(setFormValues):
      return {
        ...state,
        input: {
          ...state.input,
          [action.payload.id]: action.payload.data,
        },
      };

    case getType(updateValue):
      return {
        ...state,
        input: {
          ...state.input,
          [action.payload.form]: {
            ...state.input[action.payload.form],
            [action.payload.id]: action.payload.value,
          },
        },
      };
    case getType(updateDateValue):
      return {
        ...state,
        input: {
          ...state.input,
          [action.payload.form]: {
            ...state.input[action.payload.form],
            [action.payload.id]: action.payload.value
              ? action.payload.value.toISOString()
              : null,
          },
        },
      };
    case getType(addToValue):
      return {
        ...state,
        input: {
          ...state.input,
          [action.payload.form]: {
            ...state.input[action.payload.form],
            [action.payload.id]: action.payload.value,
          },
        },
      };

    case getType(removeKey):
      if (state.input[action.payload.form][action.payload.id]) {
        const { [action.payload.id]: remove, ...rest } = state.input[
          action.payload.form
        ];
        return {
          ...state,
          input: {
            ...state.input,
            [action.payload.form]: rest,
          },
        };
      } else {
        return state;
      }

    case getType(changeSelectedTemplate):
      return {
        ...state,
        input: {
          ...state.input,
          newCert: {},
        },
      };

    //get form
    case getType(getFormAsync.request):
      return loop(
        {
          ...state,
          initLoading: true,
        },
        Cmd.run(formService.getForm, {
          successActionCreator: getFormAsync.success,
          failActionCreator: getFormAsync.failure,
          args: [action.payload],
        })
      );
    case getType(getFormAsync.success):
      if (action.payload.status === 200) {
        return {
          ...state,
          initLoading: false,
          items: {
            ...state.items,
            [action.payload.data.formName]: action.payload.data.inputs,
          },
        };
      } else {
        return {
          ...state,
          initLoading: false,
        };
      }

    case getType(getFormAsync.failure):
      return {
        ...state,
        initLoading: false,
      };

    case getType(loginAsync.success):
      return {
        ...state,
        input: {
          ...state.input,
          loginForm: {},
          createNewEvent: {
            ...state.input.createNewEvent,
            eventOrganiser: action.payload.data.customer.customerName,
          },
        },
      };

    case getType(loginAsync.failure):
      return {
        ...state,
        input: {
          ...state.input,
          loginForm: {},
        },
      };

    //new cert send
    case getType(newCertSend.request):
      //ennen kuin mennään pidemmälle, tarkastetaan, että kaikki pakolliset kohdat on täytetty
      const allRequired = action.payload.template.postmarkTemplateDataModel.postmarkTemplateQuestions
        .filter((q) => q.required)
        .map((q) => {
          return {
            id: q.id,
            question: q.question,
            defaultValue: q.defaultValue ? true : false,
          };
        })
        .concat(
          action.payload.template.templateDataModel.templateQuestions
            .filter((q) => q.required)
            .map((q) => {
              return {
                id: q.id,
                question: q.question,
                defaultValue: q.defaultValue ? true : false,
              };
            })
        );

      const missing = allRequired
        .map((q) =>
          state.input.newCert[q.id]
            ? false
            : q.defaultValue
            ? false
            : q.question
        )
        .filter((r) => typeof r === "string");
      if (missing.length > 0) {
        return loop(
          state,
          Cmd.action({
            type: "SET_ERROR_MESSAGE_CERT_MISSING_REQUIRED_FIELD",
            payload: missing,
          })
        );
      }

      //seuraavaksi tarkistetaan, että sähköpostikentät on oikein!

      if (action.payload.certOperationType === "singleSend") {
        const templateData = action.payload.template.templateDataModel.templateQuestions.reduce(
          (map, obj) => {
            map[obj.id] = state.input.newCert[obj.id]
              ? state.input.newCert[obj.id]
              : obj.defaultValue
              ? obj.defaultValue
              : "";
            return map;
          },
          {}
        );

        const postmarkTemplateData = action.payload.template.postmarkTemplateDataModel.postmarkTemplateQuestions.reduce(
          (map, obj) => {
            map[obj.id] = obj.readOnly
              ? obj.defaultValue
              : state.input.newCert[obj.id]
              ? state.input.newCert[obj.id]
              : obj.defaultValue
              ? obj.defaultValue
              : "";
            return map;
          },
          {}
        );

        const newCertRequestObject: NewSingleCertRequest = {
          certOperationId: action.payload.certOperationId,
          certOperationType: action.payload.certOperationType,
          templateId: action.payload.template.templateId,
          preview: action.payload.preview,
          templateData,
          postmarkTemplateData,
        };

        ConsoleHelper("newSingleCertRequestObject", newCertRequestObject);

        return loop(
          { ...state },
          Cmd.run(certService.newCertSend, {
            successActionCreator: newCertSend.success,
            failActionCreator: newCertSend.failure,
            args: [newCertRequestObject],
          })
        );
      }

      ConsoleHelper("this is multi-cert-send-action payload", action.payload);
      const certData = action.payload.selected.map((person) => {
        const templateData = action.payload.template.templateDataModel.templateQuestions.reduce(
          (map, obj) => {
            map[obj.id] = obj.person
              ? person[state.input.newCert[obj.id].toString()]
              : state.input.newCert[obj.id]
              ? state.input.newCert[obj.id]
              : obj.defaultValue
              ? obj.defaultValue
              : "";
            return map;
          },
          {}
        );
        const postmarkTemplateData = action.payload.template.postmarkTemplateDataModel.postmarkTemplateQuestions.reduce(
          (map, obj) => {
            map[obj.id] = obj.person
              ? person[state.input.newCert[obj.id].toString()]
              : obj.readOnly
              ? obj.defaultValue
              : state.input.newCert[obj.id]
              ? state.input.newCert[obj.id]
              : obj.defaultValue
              ? obj.defaultValue
              : "";
            return map;
          },
          {}
        );
        return {
          templateData,
          postmarkTemplateData,
        };
      });

      ConsoleHelper("certData", certData);

      const newCertRequestObject: NewMultiCertRequest = {
        templateId: action.payload.template.templateId,
        certOperationId: action.payload.certOperationId,
        certOperationType: action.payload.certOperationType,
        preview: action.payload.preview,
        certData,
      };

      return loop(
        { ...state },
        Cmd.run(certService.newCertSend, {
          successActionCreator: newCertSend.success,
          failActionCreator: newCertSend.failure,
          args: [newCertRequestObject],
        })
      );

    case getType(newCertSend.success):
      if (
        action.payload.status === 200 &&
        action.payload.data.status.toLowerCase() !== "preview"
      ) {
        const { newCert, ...rest } = state.input;
        return { ...state, input: rest };
      } else {
        return state;
      }
    case getType(newCertSend.failure):
      return state;

    case getType(setCertTemplateDefaultValues):
      const newCert = arrayToObjectVariation(
        action.payload,
        "id",
        "defaultValue"
      );
      return {
        ...state,
        input: {
          ...state.input,
          newCert,
        },
      };

    default:
      return {
        ...state,
        initLoading: false,
      };
  }
};

export function getCreateEventFormInputs(
  state
): { [key: string]: string | string[] } {
  return state.formState.input.createNewEvent;
}

export function getFormState(state): FormState {
  return state.formState;
}

export function getPreviewFormItems(state): formInputInfo[] {
  return state.formState.items.previewRegistrationForm;
}

export function getCreateEventFormItems(state): formInputInfo[] {
  return state.formState.items.createNewEvent;
}

export function getLoginFormValues(
  state
): { username: string; password: string } {
  return {
    username: state.formState.input.loginForm.username,
    password: state.formState.input.loginForm.password,
  };
}

export function getNewCertTemplateFormValues(state): { [key: string]: any } {
  return state.formState.input.newCert ? state.formState.input.newCert : {};
}

export function selectFormInputValues(
  state,
  formName
): { [key: string]: string | string[] } {
  return state.formState.input[formName] ? state.formState.input[formName] : {};
}
